import { useContext, useMemo } from 'react';
import { ThemeContext } from '@emotion/core';
import { Interpolation } from 'emotion';
import { theme as defaultTheme } from './theme';
import { css } from '../customEmotion';

export type DefaultTheme = typeof defaultTheme;

export type ThemeColor = keyof DefaultTheme['color'];

/**
 * @todo type friendly for custom theme.
 */
export type Theme = DefaultTheme & {
  [key: string]: any;
};

export type DynamicStyle<K extends string> = (
  theme: Theme,
  deps: any[],
  classes: Classes<K>
) => Interpolation;

export type Style<K extends string> = string | DynamicStyle<K>;

export type Styles<K extends string> = ReadonlyArray<[K, Style<K>]>;

export type Classes<K extends string> = {
  [className in K]: string;
};

export { defaultTheme, ThemeContext };

export function useTheme() {
  const theme = useContext(ThemeContext) as Theme;
  return theme && theme.color ? theme : defaultTheme;
}

export function makeStyles<K extends string>(styles: Styles<K>) {
  const memoizedClasses = {} as Classes<K>;
  return function useStyles(deps: any[] = []): Classes<K> {
    const theme = useTheme();
    return useMemo(
      () => {
        const classes = {} as Classes<K>;
        styles.forEach(([className, style]) => {
          if (typeof style === 'string') {
            const resolvedClass = memoizedClasses[className] || css([style]);
            classes[className] = resolvedClass;
            memoizedClasses[className] = resolvedClass;
          } else if (typeof style === 'function') {
            const result = style(theme, deps, classes);
            classes[className] = css(
              result instanceof Array ? result : [result]
            );
          }
        });
        return classes;
      },
      deps.length ? [theme, ...deps] : [theme]
    );
  };
}
